import { useState } from 'react';
import { trackFaqToggle } from '../utils/analytics';

const faqs = [
  {
    question: 'Is my financial data safe?',
    answer:
      "Absolutely. Debt Chisel never asks for bank logins or account credentials. You manually enter your debt info, and everything is encrypted and stored securely. We don't sell your data to third parties. Ever.",
  },
  {
    question: 'Why not just use a spreadsheet?',
    answer:
      'Spreadsheets work for simple scenarios, but break down with variable income and complex portfolios. Debt Chisel handles the math, optimizes your strategy, and tracks your progress month-to-month \u2014 automatically.',
  },
  {
    question: 'How is this different from Mint or YNAB?',
    answer:
      "Mint and YNAB are budgeting tools that track all spending. Debt Chisel is laser-focused on debt elimination. We build exact payoff plans with variable-income scenarios and AI optimization. No bank sync required, so it's more private and less invasive.",
  },
  {
    question: 'What exactly does the AI hybrid strategy do?',
    answer:
      "It analyzes your full debt portfolio and creates a custom payment order that balances psychological wins (paying off small debts for motivation) with mathematical efficiency (targeting high-interest debts). It's smarter than pure avalanche or snowball alone.",
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes. Pro subscriptions are month-to-month, or annual if you prefer. Cancel anytime from your account settings. No fees, no questions asked.',
  },
  {
    question: 'Do I need a credit card for the free tier?',
    answer:
      "Nope. The free tier is truly free, forever. No credit card required to sign up. Upgrade to Pro whenever you're ready.",
  },
  {
    question: 'What happens if I miss a payment one month?',
    answer:
      "Your plan doesn't break. During your monthly check-in, enter what you actually paid \u2014 even if it's $0. Pro users get revised projections showing exactly how it affects your debt-free date. It's judgment-free accountability.",
  },
  {
    question: 'Can I share my account with family?',
    answer:
      "Debt Chisel is currently single-user only. We're not planning shared accounts right now, but we're always listening \u2014 let us know if it's something you'd use.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="px-6 py-24 bg-background">
      <div className="max-w-3xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4 text-fg">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted">Everything you need to know about Debt Chisel</p>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-border rounded-xl overflow-hidden bg-white transition-all duration-200 hover:border-accent/40 hover:shadow-md"
            >
              <button
                className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-accent/5 transition-colors cursor-pointer"
                onClick={() => {
                  const isOpening = openIndex !== index;
                  setOpenIndex(isOpening ? index : null);
                  trackFaqToggle(faq.question, isOpening ? 'open' : 'close');
                }}
              >
                <span className="font-semibold text-fg">{faq.question}</span>
                <svg
                  className={`w-5 h-5 text-accent transition-transform duration-200 flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                  openIndex === index ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="overflow-hidden">
                  <div className="px-6 pb-4 text-muted leading-relaxed">{faq.answer}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
